import React, { useEffect, useState } from "react";
import { History, Search, UserPlus, UserCog, UserX, KeyRound, Shield, Settings2, Tags, CalendarPlus, CalendarClock, ArrowRightLeft, LogIn, Trash2, ShoppingCart, Receipt, Banknote, Download, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel, Spinner } from "@/pages/shared";

const ACTION_META = {
  user_created: { icon: UserPlus, cls: "bg-emerald-100 text-emerald-800" },
  user_updated: { icon: UserCog, cls: "bg-sky-100 text-sky-800" },
  user_active_changed: { icon: UserX, cls: "bg-amber-100 text-amber-800" },
  user_password_reset: { icon: KeyRound, cls: "bg-violet-100 text-violet-800" },
  settings_updated: { icon: Settings2, cls: "bg-slate-200 text-slate-800" },
  tariff_updated: { icon: Tags, cls: "bg-indigo-100 text-indigo-800" },
  reservation_created: { icon: CalendarPlus, cls: "bg-emerald-100 text-emerald-800" },
  reservation_updated: { icon: UserCog, cls: "bg-sky-100 text-sky-800" },
  reservation_extended: { icon: CalendarClock, cls: "bg-sky-100 text-sky-800" },
  reservation_moved: { icon: ArrowRightLeft, cls: "bg-amber-100 text-amber-800" },
  reservation_checkin: { icon: LogIn, cls: "bg-teal-100 text-teal-800" },
  reservation_deleted: { icon: Trash2, cls: "bg-rose-100 text-rose-800" },
  sale_created: { icon: ShoppingCart, cls: "bg-emerald-100 text-emerald-800" },
  sale_deleted: { icon: Trash2, cls: "bg-rose-100 text-rose-800" },
  cash_closed: { icon: Receipt, cls: "bg-slate-200 text-slate-800" },
  cash_deposit_created: { icon: Banknote, cls: "bg-teal-100 text-teal-800" },
  cash_deposit_cancelled: { icon: Banknote, cls: "bg-rose-100 text-rose-800" },
};
const ROLE_CLS = {
  superadmin: "bg-rose-100 text-rose-700", admin: "bg-primary/10 text-primary",
  reception: "bg-slate-100 text-slate-700", personeel: "bg-slate-100 text-slate-700", controller: "bg-slate-100 text-slate-700",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

function ActionBadge({ action, t }) {
  const m = ACTION_META[action] || { icon: History, cls: "bg-muted text-foreground" };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
      <Icon className="h-3 w-3" />{t(`audit_a_${action}`) || action}
    </span>
  );
}

function Details({ action, details, t }) {
  if (!details || Object.keys(details).length === 0) return <span className="text-muted-foreground">—</span>;
  if (action === "user_active_changed") {
    const on = details.active === true;
    return <span className={`text-xs font-semibold ${on ? "text-emerald-600" : "text-rose-600"}`}>{on ? t("audit_activated") : t("audit_deactivated")}</span>;
  }
  const parts = [];
  if (details.role) parts.push(`${t("menu_role")}: ${t(`role_${details.role}`) || details.role}`);
  if (details.finance_class !== undefined && details.finance_class !== 0) parts.push(`${t("u_finance_class")}: ${details.finance_class}`);
  if (details.from || details.to) parts.push(`${details.from || "?"} → ${details.to || "?"}`);
  if (details.old_to && details.new_to) parts.push(`${details.old_to} → ${details.new_to}`);
  if (details.from_pitch && details.to_pitch) parts.push(`${details.from_pitch} → ${details.to_pitch}${details.date ? ` (${details.date})` : ""}`);
  if (Array.isArray(details.fields)) parts.push(details.fields.join(", "));
  if (parts.length === 0) parts.push(...Object.entries(details).map(([k, v]) => `${k}: ${String(v)}`));
  return <span className="text-xs text-muted-foreground">{parts.join(" · ")}</span>;
}

export default function AdminAuditLog() {
  const { t } = useLang();
  const [days, setDays] = useState(30);
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/audit-log/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      setLoading(true);
      api.get("/audit-log", { params: { days, action: action || undefined, q: q || undefined } })
        .then(({ data }) => setD(data))
        .catch((e) => toast.error(apiError(e.response?.data?.detail)))
        .finally(() => setLoading(false));
    }, q ? 350 : 0);
    return () => clearTimeout(id);
  }, [days, action, q]);

  const download = async (fmt) => {
    try {
      const res = await api.get("/audit-log/export", {
        params: { format: fmt, days, action: action || undefined, q: q || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `logboek.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Panel
      title={t("admin_audit")}
      testid="audit-log-panel"
      action={
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("audit_search")}
              className="h-9 w-52 pl-8" data-testid="audit-search" />
          </div>
          <select value={action} onChange={(e) => setAction(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="audit-action-filter">
            <option value="">{t("audit_all_actions")}</option>
            {(d?.actions || []).map((a) => <option key={a} value={a}>{t(`audit_a_${a}`) || a}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm font-mono" data-testid="audit-days">
            {[7, 30, 90, 365].map((n) => <option key={n} value={n}>{n} d</option>)}
          </select>
          <Button variant="outline" size="sm" className="h-9" onClick={() => download("csv")} data-testid="audit-export-csv">
            <Download className="h-4 w-4 mr-1" />{t("audit_export_csv")}
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => download("pdf")} data-testid="audit-export-pdf">
            <FileText className="h-4 w-4 mr-1" />{t("audit_export_pdf")}
          </Button>
        </div>
      }
    >
      <p className="px-5 pt-4 text-sm text-muted-foreground flex items-center gap-2" data-testid="audit-intro">
        <Shield className="h-4 w-4 text-primary" />{t("admin_audit_desc")}
      </p>
      {stats && (
        <div className="px-5 pt-4 grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="audit-stats">
          <div className="rounded-xl border border-border bg-card p-4" data-testid="audit-stat-today">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("audit_stat_today")}</p>
            <p className="mt-1 text-3xl font-bold tabular-nums">{stats.today}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4" data-testid="audit-stat-week">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("audit_stat_week")}</p>
            <p className="mt-1 text-3xl font-bold tabular-nums">{stats.week}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4" data-testid="audit-stat-actors">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">{t("audit_stat_by_actor")}</p>
            {stats.by_actor.length === 0 ? <p className="text-xs text-muted-foreground">—</p> : (
              <div className="flex flex-wrap gap-1.5">
                {stats.by_actor.map((a) => (
                  <span key={a.email} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-0.5 text-[11px]" title={a.email}>
                    <span className="max-w-[140px] truncate">{a.email}</span>
                    <span className="rounded-full bg-primary/15 text-primary font-semibold px-1.5 tabular-nums">{a.count}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="max-h-[62vh] overflow-auto cb-scroll mt-3">
        {loading && !d ? <Spinner small label={t("loading_data")} /> :
          !d || d.entries.length === 0 ? (
            <p className="px-5 py-10 text-sm text-muted-foreground text-center" data-testid="audit-empty">{t("audit_none")}</p>
          ) : (
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="sticky top-0 bg-card text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">{t("audit_when")}</th>
                  <th className="px-4 py-2 text-left font-medium">{t("audit_actor")}</th>
                  <th className="px-4 py-2 text-left font-medium">{t("audit_action")}</th>
                  <th className="px-4 py-2 text-left font-medium">{t("audit_target")}</th>
                  <th className="px-4 py-2 text-left font-medium">{t("audit_details")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {d.entries.map((r, i) => (
                  <tr key={i} data-testid={`audit-row-${i}`}>
                    <td className="px-4 py-2 text-xs whitespace-nowrap text-muted-foreground">{fmt(r.at)}</td>
                    <td className="px-4 py-2">
                      <span className="text-xs font-medium">{r.actor_email}</span>
                      {r.actor_role && <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${ROLE_CLS[r.actor_role] || "bg-muted"}`}>{t(`role_${r.actor_role}`) || r.actor_role}</span>}
                    </td>
                    <td className="px-4 py-2"><ActionBadge action={r.action} t={t} /></td>
                    <td className="px-4 py-2 text-xs">{r.target_email || r.target_label || "—"}</td>
                    <td className="px-4 py-2"><Details action={r.action} details={r.details} t={t} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </Panel>
  );
}
